import React, { useState } from 'react';
import SEO from '../../components/common/SEO';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { contactService } from '../../services/firebase/contactService';
import { emailService } from '../../services/email/emailService';
import { useSettings } from '../../hooks/useSettings';
import './Contact.css';

const Contact = () => {
  const { settings, loading: settingsLoading } = useSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await contactService.createContact(formData);
      setIsSubmitted(true);

      // Send confirmation emails
      try {
        await emailService.init();

        // Send confirmation to customer
        const customerResult = await emailService.sendContactConfirmation(formData);
        if (!customerResult.success) {
          console.warn('Contact customer email failed:', customerResult.error);
        }

        // Send notification to admin (Kirstie)
        const adminResult = await emailService.sendContactNotificationToAdmin(formData);
        if (!adminResult.success) {
          console.warn('Contact admin email failed:', adminResult.error);
        }
      } catch (emailErr) {
        console.error('Error sending contact confirmation emails:', emailErr);
        // Don't throw - contact was saved successfully
      }

      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setIsSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('There was an error submitting your message. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact KO Holistic Health Sydney | Book a Consultation"
        description="Get in touch with Kirstie Warburton at KO Holistic Health. Ask about Reiki healing, tarot readings, soul coaching, or any of our holistic health services in Sydney. We'd love to hear from you."
        url="/contact"
        keywords="contact holistic health sydney, reiki inquiry sydney, wellness consultation, kirstie warburton contact, book holistic healing, holistic health practitioner near me, energy healer contact sydney"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }]}
      />

      <div className="contact-page">
        <section className="contact-hero">
          <div className="container">
            <h1>Contact Us</h1>
            <p className="contact-hero__subtitle">
              Have a question? Want to learn more? Get in touch with Kirstie.
            </p>
          </div>
        </section>

        <section className="section contact-content">
          <div className="container">
            <div className="contact-layout">
              <div className="contact-form-wrapper">
                <Card className="contact-form-card">
                  <h2>Send Us a Message</h2>
                  <p className="form-intro">
                    For booking appointments, please use our <a href="/booking" className="booking-link">booking page</a>. For general inquiries, fill out the form below.
                  </p>

                  {isSubmitted ? (
                    <div className="success-message">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      <h3>Message Sent!</h3>
                      <p>Thank you for reaching out. Kirstie will get back to you within 24-48 hours.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="contact-form">
                      <div className="form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your.email@example.com"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="0400 000 000"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="subject">Subject *</label>
                        <select
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select a subject...</option>
                          <option value="General Inquiry">General Inquiry</option>
                          <option value="Service Information">Service Information</option>
                          <option value="Workshop/Event Inquiry">Workshop/Event Inquiry</option>
                          <option value="Corporate Wellness">Corporate Wellness</option>
                          <option value="Media/Interview Request">Media/Interview Request</option>
                          <option value="Speaking Engagement">Speaking Engagement</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="message">Message *</label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="6"
                          required
                          placeholder="Tell us how we can help..."
                        ></textarea>
                      </div>

                      {error && (
                        <div className="form-error">
                          {error}
                        </div>
                      )}

                      <Button type="submit" size="large" fullWidth disabled={isSubmitting}>
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  )}
                </Card>
              </div>

              <div className="contact-info">
                <Card className="contact-info-card">
                  <h3>Contact Information</h3>
                  <div className="contact-detail">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    <div>
                      <h4>Email</h4>
                      <a href={`mailto:${settings?.email || 'info@koholistichealth.com'}`}>
                        {settings?.email || 'info@koholistichealth.com'}
                      </a>
                    </div>
                  </div>

                  <div className="contact-detail">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    <div>
                      <h4>Phone</h4>
                      <a href={`tel:${settings?.phoneFormatted || '+61439386551'}`}>
                        {settings?.phone || '0439 386 551'}
                      </a>
                    </div>
                  </div>

                  <div className="contact-detail">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    <div>
                      <h4>Location</h4>
                      <p>
                        {settings?.city || 'Sydney'}, {settings?.state || 'NSW'}<br />
                        {settings?.country || 'Australia'}
                      </p>
                    </div>
                  </div>

                  <div className="contact-detail">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    <div>
                      <h4>Availability</h4>
                      <p>
                        {settings?.availability || 'Monday - Saturday'}<br />
                        {settings?.availabilityDetail || 'Flexible hours available'}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="social-card">
                  <h3>Connect With {settings?.ownerName?.split(' ')[0] || 'Kirstie'}</h3>
                  <p>Follow {settings?.ownerName?.split(' ')[0] || 'Kirstie'}'s journey and stay updated with healing tips, event announcements, and inspiration.</p>
                  <div className="social-links">
                    {settings?.instagram && (
                      <a href={settings.instagram} target="_blank" rel="noopener noreferrer" className="social-link">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                        Instagram
                      </a>
                    )}
                    {settings?.facebook && (
                      <a href={settings.facebook} target="_blank" rel="noopener noreferrer" className="social-link">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        Facebook
                      </a>
                    )}
                    {settings?.linkedin && (
                      <a href={settings.linkedin} target="_blank" rel="noopener noreferrer" className="social-link">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                        LinkedIn
                      </a>
                    )}
                    {settings?.youtube && (
                      <a href={settings.youtube} target="_blank" rel="noopener noreferrer" className="social-link">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                        </svg>
                        YouTube
                      </a>
                    )}
                  </div>
                </Card>

                <div className="faq-card">
                  <h3>Frequently Asked Questions</h3>
                  <div className="faq-item">
                    <h4>What's a discovery call?</h4>
                    <p>A free 15-minute phone or Zoom call to discuss your needs and find the right service for you.</p>
                  </div>
                  <div className="faq-item">
                    <h4>Do you offer online sessions?</h4>
                    <p>Yes! Most services are available both in-person and via Zoom.</p>
                  </div>
                  <div className="faq-item">
                    <h4>What's your cancellation policy?</h4>
                    <p>Please provide 24 hours notice for cancellations or rescheduling.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
